import { SectionCard } from "shared/app/SectionCard";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { propertyTypeOptions } from "../AddProperty/constants";
import { Select, Show, TextArea } from "shared/components";
import type { Option } from "shared/components/forms/Select";
import { usePlacesWidget } from "react-google-autocomplete";
import { googlePlaceToAddress } from "shared/addressUtils";
import config from "config/config";
import { states } from "utils/stateCodes";
import { ReactNode, useMemo } from "react";
import { useAuthContext } from "providers/Auth";
import { PartnerFeatures, PropertyKind } from "shared/types";
import { useFormTyped, type RegisterTyped } from "shared/hooks/use-form-typed";

type PropertyGeneralFormFields = {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state_or_province: string;
  postal_code: string;
  country?: string;
  owner_name?: string | null;
  description?: string;
  external_id?: string;
  kind?: PropertyKind | null;
};

type HandleChangeTyped<T extends Record<string, unknown>> = ReturnType<
  typeof useFormTyped<T>
>["handleChange"];

interface PropertyGeneralFormProps<T extends PropertyGeneralFormFields> {
  headerExtra?: ReactNode;
  handleChange: HandleChangeTyped<T>;
  register: RegisterTyped<T>;
  footer?: ReactNode;
  isDefaultProperty?: boolean;
  propertyKindDisabled?: boolean;
  pmsLocked?: boolean;
  extraSections?: ReactNode;
}

export const PropertyGeneralForm = <T extends PropertyGeneralFormFields>({
  handleChange,
  register,
  headerExtra,
  footer,
  isDefaultProperty,
  propertyKindDisabled,
  pmsLocked = false,
  extraSections
}: PropertyGeneralFormProps<T>) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { type, partnerFeatures } = useAuthContext();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );
  const kindOptions = useMemo<Option[]>(
    () =>
      propertyTypeOptions.map((opt) =>
        opt.value === PropertyKind.ManufacturedHousing
          ? { ...opt, label: "Mobile Home" }
          : opt
      ) as Option[],
    []
  );

  const { ref } = usePlacesWidget({
    apiKey: config.placesApiKey,
    onPlaceSelected: (place) => {
      const addressFields = googlePlaceToAddress(place);
      if (addressFields) {
        handleChange("address1")(addressFields.address1 || "");
        handleChange("postal_code")(addressFields.postalCode || "");
        handleChange("city")(addressFields.city || "");
        handleChange("state_or_province")(addressFields.stateOrProvince || "");
        handleChange("country")(addressFields.country || "");
      }
    },
    options: {
      types: ["street_address"],
      fields: ["formatted_address", "address_components"],
      componentRestrictions: { country: "us" }
    }
  });

  return (
    <SectionCard title="General" headerExtra={headerExtra} footer={footer}>
      <div className="divide-y divide-alpha-40">
        <div className="grid grid-cols-2 gap-4 pb-4">
          <div>
            <BaseInput
              id="property-name"
              label={`${dictionary.Property} name`}
              required
              placeholder="Lemon Grove Apartments"
              disabled={pmsLocked}
              {...register("name")}
            />
          </div>

          <Show if={type.isBoomCRM}>
            <div>
              <Select
                id="property-type"
                label={`${dictionary.Property} type`}
                required
                defaultValue={propertyTypeOptions[0].value ?? undefined}
                options={kindOptions}
                isDisabled={propertyKindDisabled}
                {...register("kind")}
              />
            </div>
          </Show>

          <Show if={hasPropertyOwner}>
            <div>
              <BaseInput
                id="property-owner-name"
                label="Owner"
                placeholder="Rentex"
                required
                disabled={pmsLocked}
                {...register("owner_name")}
              />
            </div>
          </Show>
        </div>

        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="w-full">
            <BaseInput
              id="selected-place"
              label="Street address"
              required
              placeholder="2972 Westheimer Rd"
              ref={ref}
              disabled={pmsLocked}
              {...register("address1")}
            />
          </div>
          <div className="w-full">
            <BaseInput
              id="address2"
              placeholder="Apartment 12"
              label="Apartment, suite, etc."
              disabled={pmsLocked}
              {...register("address2")}
            />
          </div>
          <div className="w-full">
            <BaseInput
              id="city"
              label="City"
              required
              placeholder="Austin"
              disabled={pmsLocked}
              {...register("city")}
            />
          </div>
          <div className="flex w-full justify-end gap-4">
            <div className="mb-auto h-fit flex-1">
              <Select
                id="state-or-province"
                label="State"
                placeholder="State"
                searchable
                hideOptional
                options={states}
                isDisabled={pmsLocked}
                {...register("state_or_province")}
                showErrorMessage
              />
            </div>
            <div className="flex-1">
              <BaseInput
                id="postal-code"
                required
                label="Zip"
                placeholder="11111"
                disabled={pmsLocked}
                {...register("postal_code")}
              />
            </div>
          </div>
        </div>

        {isDefaultProperty ? null : (
          <div className="py-4">
            <TextArea
              id="property-description"
              label="Description"
              placeholder="Add any additional details here"
              rows={5}
              disabled={pmsLocked}
              {...register("description")}
            />
          </div>
        )}

        <div className="pt-4 grid grid-cols-2 gap-4">
          <div className="w-full">
            <BaseInput
              id="property-id"
              label={`${dictionary.Property} ID`}
              placeholder="434343"
              disabled={pmsLocked}
              {...register("external_id")}
            />
          </div>
        </div>

        {extraSections}
      </div>
    </SectionCard>
  );
};
